import React, { useState } from 'react';
import { Typography } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { FctGlifoAtencion, FctIcono } from '../../../fct/react';
import { fct } from '../../../fct/tokens/tokens';

const { Text } = Typography;

export type TipoNota = 'ok' | 'info' | 'aviso' | 'error';

/**
 * Marca por tipo (R-14, iconos en una tinta): lo correcto lleva el icono «completado» del manual
 * sin color propio; el resto es atención y va con el glifo del manual, que lleva su tinta por nivel.
 */
const ESTILO: Record<TipoNota, { icono: React.ReactNode; etiqueta: string }> = {
  ok: { icono: <FctIcono nombre="completado" style={{ width: 14, height: 14 }} />, etiqueta: 'Correcto' },
  info: { icono: <FctGlifoAtencion nivel="info" />, etiqueta: 'Información' },
  aviso: { icono: <FctGlifoAtencion nivel="atencion" />, etiqueta: 'Aviso' },
  error: { icono: <FctGlifoAtencion nivel="urgente" />, etiqueta: 'Error' },
};

interface NotaProcesoProps {
  tipo: TipoNota;
  /** Una línea: lo que hay que saber. */
  titulo: React.ReactNode;
  /** Lo que explica o demuestra la línea. Solo se ve si el operador lo pide. */
  detalle?: React.ReactNode;
  /** Abierta al aparecer: solo para lo que el operador tiene que leer sí o sí (el bloqueo). */
  abierta?: boolean;
}

/**
 * Nota discreta del panel de proceso (Dev, 2026-09-24: «el proceso es lo más difícil de seguir;
 * aparecen cuadros de distinto tamaño que desplazan la información fija»). Una sola línea con
 * icono; el detalle queda plegado y se abre a demanda, así que una nota nueva ocupa siempre lo
 * mismo y no empuja lo de debajo. Sustituye a las cajas `Alert` con descripción del asistente.
 */
export const NotaProceso: React.FC<NotaProcesoProps> = ({ tipo, titulo, detalle, abierta = false }) => {
  const [abiertaAhora, setAbiertaAhora] = useState(abierta);
  const e = ESTILO[tipo];
  const conmutar = () => setAbiertaAhora((v) => !v);
  return (
    <div role={tipo === 'error' ? 'alert' : 'status'} style={{ fontSize: 12, lineHeight: '18px' }}>
      {/* Con detalle, toda la línea se pulsa, y un chevron pequeño junto a la frase dice que se
          despliega (Dev, 2026-09-24: sin el enlace «detalle»). */}
      <div
        role={detalle ? 'button' : undefined}
        tabIndex={detalle ? 0 : undefined}
        aria-expanded={detalle ? abiertaAhora : undefined}
        onClick={detalle ? conmutar : undefined}
        onKeyDown={detalle ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); conmutar(); } } : undefined}
        style={{ display: 'flex', alignItems: 'flex-start', gap: 6, cursor: detalle ? 'pointer' : 'default' }}
      >
        <span style={{ marginTop: 1, display: 'inline-flex' }} aria-label={e.etiqueta}>{e.icono}</span>
        <Text style={{ flex: 1, fontSize: 12, color: fct.tinta['900'] }}>
          {titulo}
          {detalle && (
            <span style={{ color: fct.tinta['700'], fontSize: 9, marginLeft: 6, verticalAlign: 'middle' }} aria-hidden>
              {abiertaAhora ? <DownOutlined /> : <RightOutlined />}
            </span>
          )}
        </Text>
      </div>
      {detalle && abiertaAhora && (
        <div style={{ margin: '4px 0 2px 18px', color: fct.tinta['700'], fontSize: 12 }}>{detalle}</div>
      )}
    </div>
  );
};
